/**
 * Templates Page
 *
 * Displays all process templates with filtering, editing, and creation.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, Space, Button, Switch } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { templatesApi, categoriesApi } from '@api/index';
import { TemplatesGrid, type ActiveFilter } from '@components/grid';
import { WorkPackageDrawer } from '@components/drawers';
import type { ProcessTemplate } from '@/types';

const { Text } = Typography;

export default function TemplatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial template ID from URL
  const initialTemplateId = searchParams.get('id');

  // Parse saved view filters from URL
  const initialFilters = useMemo((): ActiveFilter[] => {
    const filtersParam = searchParams.get('filters');
    if (!filtersParam) return [];
    try {
      return JSON.parse(filtersParam);
    } catch {
      return [];
    }
  }, [searchParams]);

  const [showInactive, setShowInactive] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(!!initialTemplateId);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(initialTemplateId || undefined);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit' | 'create'>(initialTemplateId ? 'edit' : 'view');

  // Open template from URL parameter
  useEffect(() => {
    if (initialTemplateId) {
      setSelectedTemplateId(initialTemplateId);
      setDrawerOpen(true);
      setDrawerMode('edit');
    }
  }, [initialTemplateId]);

  // Fetch templates with stats
  const { data: templates = [], isLoading, refetch } = useQuery({
    queryKey: ['templates', 'with-stats'],
    queryFn: () => templatesApi.listWithStats({}),
  });

  // Filter by active status if needed
  const filteredTemplates = useMemo(() =>
    showInactive ? templates : templates.filter((t) => t.isActive),
    [templates, showInactive]
  );

  // Fetch categories for filter
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  // Single handler for row click - opens directly in edit mode
  const handleSelectTemplate = useCallback((template: ProcessTemplate) => {
    setSelectedTemplateId(template.id);
    setDrawerMode('edit');
    setDrawerOpen(true);
    setSearchParams({ id: template.id });
  }, [setSearchParams]);

  const handleCreateTemplate = useCallback(() => {
    setSelectedTemplateId(undefined);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedTemplateId(undefined);
    setSearchParams({});
  }, [setSearchParams]);

  const handleDrawerSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  // Extra toolbar buttons
  const extraButtons = useMemo(() => (
    <Space>
      <Space size={4}>
        <Text type="secondary" style={{ fontSize: 12 }}>Show inactive:</Text>
        <Switch checked={showInactive} onChange={setShowInactive} size="small" />
      </Space>
      <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading} />
      <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTemplate}>
        New
      </Button>
    </Space>
  ), [showInactive, isLoading, refetch, handleCreateTemplate]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Grid with integrated toolbar */}
      <div style={{ flex: 1 }}>
        <TemplatesGrid
          templates={filteredTemplates}
          loading={isLoading}
          onSelectTemplate={handleSelectTemplate}
          categories={categories}
          extraToolbarButtons={extraButtons}
          initialFilters={initialFilters}
        />
      </div>

      {/* Work Package Drawer */}
      <WorkPackageDrawer
        open={drawerOpen}
        itemId={selectedTemplateId}
        mode={drawerMode}
        isTemplate={true}
        onClose={handleCloseDrawer}
        onSaved={handleDrawerSaved}
      />
    </div>
  );
}
